import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";

export const useFetchStage = (stageCode) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  const URL = `${sport}/o/${organizationId}/seasons/${seasonId}/stages/${stageCode}`;

  return useQuery({
    queryKey: ["stages", seasonId, stageCode],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data?.[0];
    },
    enabled: !!stageCode,
  });
};

export const useFetchStagesPagination = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  let URL = `${sport}/o/${organizationId}/seasons/${seasonId}/stages`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["stages", seasonId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useFetchStages = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  let URL = `${sport}/o/${organizationId}/seasons/${seasonId}/stages`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["stages", seasonId, JSON.stringify(params)],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data || [];
    },
  });
};

export const useDeleteStage = () => {
  const { sport, organizationId, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (stageCode) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}/stages/${stageCode}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "stage.delete.success",
          defaultMessage: "Stage deleted successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["stages"] });
    },
  });
};

export const useCreateStage = () => {
  const { mutateData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      const result = await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}/stages`,
        data,
      });
      return result?.data?.[0];
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "stage.add.success",
          defaultMessage: "Stage added successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["stages"] });
    },
  });
};

export const useUpdateStage = () => {
  const { mutateData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({ data, stageCode }) => {
      const result = await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}/stages/${stageCode}`,
        method: "PUT",
        data,
      });
      return result?.data?.[0];
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "stage.update.success",
          defaultMessage: "Stage updated successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["stages"] });
    },
  });
};
